export interface WorkflowPage<T> {
  items: T[]
  total: number
  limit: number
  offset: number
}
export type InspectionState = "unspecified" | "draft" | "in_progress" | "completed" | "cancelled"
export interface InspectionLifecycle {
  inspectionUuid: string
  state: InspectionState
  version: number
  snapshotUuid?: string
}
export interface InspectionFinding {
  category: { code: string; label: string; iconKey: string; sortOrder: number }
  categorySuggested: boolean
  answerUuid: string | null
  equipmentUuid: string | null
  assetUuid: string | null
  componentUuid: string | null
  contextUuid: string | null
  contextKind: string
  equipmentName: string
  contextLabel: string
  pointUuid: string
  revisionUuid: string | null
  answerRevisionUuid: string | null
  definition: Record<string, unknown>
  status: number | null
  comment: string
  measurement: string
  severity: string
  recommendation: string
  immediateAction: string
  dueDate: string | null
  photoException: string
  pictures: string[]
}
export interface InspectionSnapshot {
  uuid?: string
  inspectionUuid: string
  physicalSiteUuid: string | null
  missionName: string
  siteName: string
  normType: number
  executionDate: string | null
  capturedAt: string
  fingerprint: string
  frozen: boolean
  warnings: string[]
  findings: InspectionFinding[] | null
}
export interface InspectionDifference {
  key: string
  kind: string
  before: InspectionFinding | null
  after: InspectionFinding | null
}
export interface InspectionComparison {
  before: InspectionSnapshot
  after: InspectionSnapshot
  items: InspectionDifference[]
  counts: Record<string, number>
  total: number
  limit: number
  offset: number
}
export type ActionStatus = "open" | "in_progress" | "awaiting_verification" | "closed" | "cancelled"
export interface CorrectiveAction {
  uuid: string
  physicalSiteUuid: string
  assetUuid: string | null
  componentUuid: string | null
  title: string
  description: string
  priority: string
  status: ActionStatus
  assigneeUuid: string | null
  dueDate: string | null
  version: number
  createdAt: string
  updatedAt: string
}
export interface CorrectiveActionWrite {
  uuid: string
  expectedVersion?: number
  assetUuid: string | null
  componentUuid: string | null
  title: string
  description: string
  priority: string
  status: ActionStatus
  assigneeUuid: string | null
  dueDate: string | null
  sourceAnswerUuid?: string
  reason: string
}
export interface ActionObservation {
  uuid: string
  sourceAnswerUuid: string
  inspectionUuid: string
  finding: InspectionFinding
  createdAt: string
}
export interface ActionEvidence {
  uuid: string
  name: string
  mimeType: string
  createdAt: string
}
export interface ActionDetail {
  action: CorrectiveAction
  observations: ActionObservation[]
  evidence: ActionEvidence[]
  canVerify: boolean
}
export interface WorkflowEvent {
  uuid: string
  subjectKind: string
  subjectUuid: string
  actorUuid: string
  reason: string
  payload: unknown
  createdAt: string
}
export interface InspectionPlan {
  uuid: string
  physicalSiteUuid: string
  title: string
  normType: number
  methodType: number
  anchorDate: string
  intervalMonths: number
  assigneeUuid: string
  templateUuid: string | null
  templateRevisionUuid: string | null
  reminderDays: number
  archived: boolean
  version: number
}
export type InspectionPlanWrite = Omit<InspectionPlan, "version"> & {
  expectedVersion?: number
  reason: string
}
export interface PlannedInspection {
  uuid: string
  planUuid: string
  physicalSiteUuid: string
  title: string
  originalDate: string
  dueDate: string
  status: "planned" | "linked" | "cancelled"
  inspectionUuid: string | null
  inspectionState: InspectionState
  normType: number
  assigneeUuid: string
  reminderDays: number
  planVersion: number
  version: number
  planArchived: boolean
}
export interface SiteWorkflowOverview {
  physicalSiteUuid: string
  enterpriseUuid: string
  name: string
  archived: boolean
  openActions: number
  overdueActions: number
  upcomingInspections: number
  overdueInspections: number
}
export interface AnswerFollowUp {
  physicalSiteUuid: string
  enterpriseUuid: string
  inspectionUuid: string
  finding: InspectionFinding
  actions: CorrectiveAction[]
}
export interface WorkflowUser {
  UUID: string
  FirstName: string
  LastName: string
  Username: string
  IsActive?: boolean
}
export const inspectionStates: Record<InspectionState, string> = {
  unspecified: "État historique non renseigné",
  draft: "Brouillon",
  in_progress: "En cours",
  completed: "Terminée",
  cancelled: "Annulée",
}
export const actionStates: Record<ActionStatus, string> = {
  open: "Ouverte",
  in_progress: "En cours",
  awaiting_verification: "À vérifier",
  closed: "Clôturée",
  cancelled: "Annulée",
}
export const differenceLabels: Record<string, string> = {
  unchanged: "Inchangé",
  improved: "Amélioration",
  worsened: "Dégradation",
  status_changed: "Résultat modifié",
  details_changed: "Détails modifiés",
  definition_changed: "Définition modifiée",
  unknown_revision: "Révision inconnue / non confirmée",
  unanswered: "Sans réponse exploitable",
  added: "Ajout au périmètre",
  removed: "Retrait du périmètre",
  missing_identity: "Correspondance manquante",
  ambiguous_identity: "Identité ambiguë",
}
export const statusLabels: Record<number, string> = {
  0: "Non renseigné",
  1: "Sans objet",
  2: "Conforme",
  3: "Non conforme",
  4: "À contrôler",
  5: "Observation",
  6: "Non vérifié",
}
export function isOverdue(
  date: string | null,
  status: string,
  today = new Date().toISOString().slice(0, 10),
) {
  return !!date && date < today && !["closed", "cancelled", "completed"].includes(status)
}
export function actionTransitions(status: ActionStatus): ActionStatus[] {
  const next: Record<ActionStatus, ActionStatus[]> = {
    open: ["in_progress", "awaiting_verification", "cancelled"],
    in_progress: ["open", "awaiting_verification", "cancelled"],
    awaiting_verification: ["in_progress", "closed", "cancelled"],
    closed: ["open"],
    cancelled: ["open"],
  }
  return [status, ...next[status]]
}
